import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ApplicationForm, IPCvData } from '../types';

/**
 * Hook to synchronize ipCvData with the current team composition
 * Ensures that ipCvData always reflects the current Principal Investigators
 */
export const useIPSync = (setUpdating?: (updating: boolean) => void) => {
  const { watch, setValue } = useFormContext<ApplicationForm>();

  const principalInvestigator = watch('principalInvestigator');
  const coInvestigators = watch('coInvestigators');
  const ipCvData = watch('ipCvData');

  // Normalize RUN by removing leading zeros (matches backend logic)
  const normalizeRun = useCallback((run: string): string => {
    if (!run) return '';

    // Split by dash to handle number and check digit separately
    const parts = run.split('-');
    if (parts.length !== 2) {
      return run; // Return as-is if format is unexpected
    }

    const [number, checkDigit] = parts;
    // Remove leading zeros from the number part
    const cleanNumber = number ? number.replace(/^0+/, '') || '0' : '';

    return `${cleanNumber}-${checkDigit}`;
  }, []);

  // Get all UC researchers who need CV + publications (IR, IP, IA)
  const getAllIPs = useCallback(() => {
    const ips: Array<{ id: string; name: string; role: 'IR' | 'IP' | 'IA' }> = [];

    if (principalInvestigator?.name) {
      ips.push({
        id: normalizeRun(principalInvestigator.run || 'principal'),
        name: principalInvestigator.name,
        role: 'IR',
      });
    }

    if (coInvestigators) {
      coInvestigators.forEach((coInvestigator, index) => {
        if (coInvestigator.role === 'IP' || coInvestigator.role === 'IA') {
          ips.push({
            id: normalizeRun(coInvestigator.run || `co-${coInvestigator.name ?? index}`),
            name: coInvestigator.name,
            role: coInvestigator.role,
          });
        }
      });
    }

    return ips;
  }, [principalInvestigator, coInvestigators, normalizeRun]);

  // Sync ipCvData with current IPs
  useEffect(() => {
    const allIPs = getAllIPs();
    const currentIpCvData = ipCvData || [];
    const newIpCvData: IPCvData[] = [];

    allIPs.forEach((ip) => {
      const existingData = currentIpCvData.find(
        (data) => normalizeRun(data.investigatorId) === normalizeRun(ip.id)
      );

      if (existingData) {
        newIpCvData.push({
          ...existingData,
          investigatorId: ip.id,
          investigatorName: ip.name,
          role: ip.role,
        });
      } else {
        newIpCvData.push({
          investigatorId: ip.id,
          investigatorName: ip.name,
          role: ip.role,
          cvNarrative: '',
          publications: [],
        });
      }
    });

    if (JSON.stringify(currentIpCvData) !== JSON.stringify(newIpCvData)) {
      // Prevent auto-save during synchronization if setUpdating is available
      if (setUpdating) {
        setUpdating(true);
      }

      setValue('ipCvData', newIpCvData);

      if (setUpdating) {
        setUpdating(false);
      }
    }
  }, [getAllIPs, ipCvData, setValue, setUpdating, normalizeRun]);

  return { getAllIPs };
};